import { Card, Space, Tag, Input, Row, Col, Popconfirm, Divider, Form, Button, Select, message, Spin } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { Context } from "../../context";
import { languageOptions } from "./languageOptions";


const SubtitlesList = ({
  existingSubtitleTracks,
  handleDeleteTrack,
}) => {
  return (
    <Space direction="vertical" style={{ width: "100%" }}>

      {/* Table Header */}
      <Row
        align="middle"
        style={{
          fontWeight: "bold",
          background: "#fafafa",
          padding: "8px 12px",
          borderRadius: "8px",
        }}
      >
        <Col span={8}>Language Name</Col>
        <Col span={8} style={{ textAlign: "center" }}>Status</Col>
        <Col span={8} style={{ textAlign: "center" }}>Actions</Col>
      </Row>

      {/* Table Body */}
      {existingSubtitleTracks.map((track) => (
        <Row
          key={track.id}
          align="middle"
          style={{
            marginBottom: 8,
            padding: "8px 12px",
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
          }}
        >
          {/* Language Name */}
          <Col span={8}>
            <span>
              {track.languageName}
            </span>
          </Col>

          {/* Status */}
          <Col span={8} style={{ textAlign: "center" }}>
            <Tag color="blue" style={{ textTransform: "capitalize" }}>
              {track.status}
            </Tag>
          </Col>

          {/* Actions */}
          <Col span={8} style={{ textAlign: "center" }}>
            <Space>
              <Popconfirm
                placement="top"
                title={`Do you want to delete ${track.languageName} subtitle track?`}
                onConfirm={() => handleDeleteTrack(track.id)}
                okText="Delete"
                cancelText="Cancel"
                okType="danger"
                icon={null}
              >
                <DeleteOutlined
                  style={{
                    fontSize: "16px",
                    color: "red",
                    cursor: "pointer",
                  }}
                />
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      ))}
    </Space>
  );
};

const SubtitleTracks = ({
  selectedContent,
}) => {
  const [subtitleTrackForm] = Form.useForm();
  const [addingSubtitleTrack, setAddingSubtitleTrack] = useState(false);
  const [editingSubtitleTrack, setEditingSubtitleTrack] = useState(null);
  const [existingSubtitles, setExistingSubtitles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subtitlesDownloadUrl, setSubtitlesDownloadUrl] = useState();

  const { token, apiUrl } = useContext(Context);

  const fetchTracks = useCallback(async () => {

    setLoading(true);
    setError(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const params = new URLSearchParams({
        content_type: selectedContent?.contentType,
        track_type: "subtitle",
      });

      if (selectedContent.contentType === "movie" && selectedContent.contentId) {
        params.append("content_id", selectedContent.contentId);
      }

      if (selectedContent.contentType === "episode" && selectedContent.seriesId && selectedContent.season && selectedContent.number) {
        params.append("series_id", selectedContent.seriesId);
        params.append("season", selectedContent.season);
        params.append("episode", selectedContent.number);
      }

      const res = await fetch(
        `${apiUrl}/fpvideo/get_tracks?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to fetch tracks");
      }

      const data = await res.json();
      const fieldName = "subtitleTracksFp"

      setExistingSubtitles(data[fieldName] || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }

  }, [apiUrl, selectedContent, token]);

  useEffect(() => {
    const audioTracks = selectedContent?.subtitleTracksFp?.length >= 1 ? selectedContent?.subtitleTracksFp : null;
    setExistingSubtitles(audioTracks);
    const url = apiUrl.includes("ibee.ai") ? "https://stream.fastpix.io" : "https://stream.fastpix.app";
    setSubtitlesDownloadUrl(url);
  }, [apiUrl, selectedContent]);


  // Handle subtitle track addition/editing
  const handleSubtitleTrackSubmit = async (values) => {
    if (!selectedContent) {
      message.error('Please select content first');
      return;
    }

    try {
      setAddingSubtitleTrack(true);

      const selectedLanguage = languageOptions.find(option => option.value === values.subtitleLanguage);

      const payload = {
        contentId: selectedContent.contentId,
        mediaId: selectedContent.mediaIdFp,
        url: values.subtitleUrl,
        languageCode: values.subtitleLanguage,
        languageName: selectedLanguage.label,
        type: "subtitle",
        isEdit: !!editingSubtitleTrack
      };

      if (payload.isEdit) {
        payload.trackId = editingSubtitleTrack?.id;
      }

      const response = await fetch(`${apiUrl}/fpvideo/manage_tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.success) {
        message.success(editingSubtitleTrack ? 'Subtitle track updated successfully!' : 'Subtitle track added successfully!');
        subtitleTrackForm.resetFields();
        setEditingSubtitleTrack(null);
        fetchTracks();

      } else {
        message.error(result.error.error.description || result.error.error.message || 'Failed to manage subtitle track');
      }
    } catch (error) {
      message.error('Network error: Failed to manage subtitle track. Please check your connection and try again.');
    } finally {
      setAddingSubtitleTrack(false);
    }
  };

  // Handle track deletion
  const handleDeleteTrack = async (trackId) => {
    try {

      const requestBody = {
        "content_type": selectedContent.contentType,
        "content_id": selectedContent.key
      }

      if (selectedContent.contentType === "episode") {
        requestBody.series_id = selectedContent.seriesId;
        requestBody.season_number = selectedContent.seasonNumber;
        requestBody.episode_number = selectedContent.episodeNumber;
      }

      const response = await fetch(`${apiUrl}/fpvideo/delete_track/${selectedContent.mediaIdFp}/${trackId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      if (result.success) {
        message.success('Subtitle track deleted successfully!');
        fetchTracks();
      } else {
        message.error(result.error.error.description || result.error.error.message || 'Failed to delete subtitle track');
      }
    } catch (error) {
      message.error('Network error: Failed to delete subtitle track. Please check your connection and try again.');
    }
  };

  return (
    <Card title="Subtitle Tracks" style={{ width: 500 }}>
      <Form
        form={subtitleTrackForm}
        onFinish={handleSubtitleTrackSubmit}
        layout="vertical"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="subtitleLanguage"
              label="Language"
              rules={[{ required: true, message: 'Please select language' }]}
            >
              <Select
                placeholder="Select language"
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                disabled={addingSubtitleTrack}
              >
                {languageOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="subtitleUrl"
              label="Subtitle URL"
              rules={[
                { required: true, message: 'Please enter subtitle URL' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input
                placeholder="https://example.com/subtitle.vtt"
                disabled={addingSubtitleTrack}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={addingSubtitleTrack}
              disabled={addingSubtitleTrack || loading || !selectedContent?.mediaIdFp}
            >
              Add Subtitle Track
            </Button>

          </Space>
        </Form.Item>
      </Form>

      <Divider />

      {loading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin tip="Loading subtitles..." />
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", color: "red" }}>
          {error}
        </div>
      ) : existingSubtitles && existingSubtitles.length > 0 ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <SubtitlesList
            existingSubtitleTracks={existingSubtitles}
            selectedContent={selectedContent}
            handleDeleteTrack={handleDeleteTrack}
          />
        </Space>
      ) : (
        <div style={{ textAlign: 'center', color: '#999' }}>
          No subtitle tracks available
        </div>
      )}
    </Card>
  );
};

export default SubtitleTracks; 
